// The Apogee breakdown: every word with its points and how they were made
// (the stars' points, the percentages on the constellation, the standings),
// the points the alignments add to the play, the final percentage, and the
// total; then the chosen alignments. During play it reads as the score so
// far; once finished, as the final score.
import type { PlayScore } from '@wits/core';

interface Props {
  score: PlayScore;
  /** The server's final score once the play is finished; null during play. */
  final: number | null;
}

export function ScoreTable({ score, final }: Props) {
  const finished = final !== null;
  return (
    <div className="score-table">
      {score.words.length === 0 && <p>No constellations yet.</p>}
      {score.words.map((w) => (
        <div key={w.word} className="score-row">
          <span className="score-word">
            <span className="mono">{w.word}</span>
            <span className="score-how mono">
              {w.letters} pts{w.additions > 0 ? ` + ${w.additions}` : ''}
              {w.percent > 0 ? ` · +${w.percent}%` : ''}
              {w.planet ? ' · planet' : ''}
              {w.standing?.zeroed ? ' · 0 in the standings' : w.standing?.copiedFrom ? ` · as ${w.standing.copiedFrom}` : w.standing?.percent ? ` · +${w.standing.percent}% standing` : ''}
            </span>
          </span>
          <span className="score-points mono">+{w.total}</span>
        </div>
      ))}
      {score.playAdditions.map((a) => (
        <div key={a.alignment} className="score-row">
          <span className="score-word">
            {a.label}
            <span className="score-how mono">
              {a.count} × {a.each}
            </span>
          </span>
          <span className="score-points mono">+{a.total}</span>
        </div>
      ))}
      {score.finalPercent > 0 && (
        <div className="score-row">
          <span className="score-word">{score.alignments.find((a) => a.active && a.alignment === 'surety')?.name ?? 'Final'}</span>
          <span className="score-points mono">+{score.finalPercent}%</span>
        </div>
      )}
      <div className="score-row is-total">
        <span className="score-word">{finished ? 'Score' : 'So far'}</span>
        <span className="score-points mono">{final ?? score.running}</span>
      </div>
      {score.alignments.length > 0 && (
        <div className="score-alignments">
          {score.alignments.map((a) => (
            <div key={a.planet.id} className={a.active ? 'score-row' : 'score-row is-inactive'}>
              <span className="score-word">
                {a.name}
                <span className="score-how">{a.text}</span>
              </span>
              <span className="score-points mono">{a.active ? `+${a.points}` : 'inactive'}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
